import React from 'react';
import { Row, Col, Card, Space, Typography, Skeleton, Tooltip, Grid } from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    BankOutlined,
    WalletOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

export default function MovimentacaoFinanceiraStats({ stats, loading, pendenciesCount = 0 }) {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const {
        creditosMes = 0,
        debitosMes = 0,
        saldoAnterior = 0,   // Saldo Inicial (fechamento anterior: 2025 Portal + 2026 Pantheon)
        saldoMes = 0         // Saldo Final = inicial + créditos − débitos
    } = stats || {};

    // Saldo final muda de cor conforme o sinal.
    const finalColor = saldoMes >= 0 ? '#059669' : '#e11d48';
    const finalBg = saldoMes >= 0 ? '#ecfdf5' : '#fff1f2';

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { 
            style: 'currency', 
            currency: 'BRL',
            minimumFractionDigits: 2
        }).format(val);
    };

    const StatCard = ({ title, value, icon, color, bgColor, delay }) => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay, duration: 0.4 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            style={{ height: '100%' }}
        >
            <Card 
                bordered={false} 
                bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
                style={{ 
                    borderRadius: '16px', 
                    height: '100%',
                    background: '#ffffff',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                    border: '1px solid #f1f5f9'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16 }}>
                    <div style={{
                        width: isMobile ? 40 : 48,
                        height: isMobile ? 40 : 48,
                        borderRadius: '12px',
                        backgroundColor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: color,
                        fontSize: isMobile ? '18px' : '22px',
                        flexShrink: 0
                    }}>
                        {icon}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ 
                            fontSize: isMobile ? '10px' : '11px', 
                            color: '#64748b', 
                            fontWeight: 700, 
                            textTransform: 'uppercase', 
                            letterSpacing: '0.05em', 
                            marginBottom: 4 
                        }}>
                            {title}
                        </div>
                        <div style={{
                            fontSize: isMobile ? '16px' : '20px',
                            fontWeight: 800,
                            color: '#0f172a',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {formatCurrency(value)}
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );

    return (
        <div style={{ marginBottom: 24 }}>
            <Skeleton loading={loading} active avatar paragraph={{ rows: 1 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Saldo Inicial"
                            value={saldoAnterior}
                            icon={<BankOutlined />}
                            color="#4f46e5"
                            bgColor="#eef2ff"
                            delay={0.05}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Créditos Mês"
                            value={creditosMes}
                            icon={<ArrowUpOutlined />}
                            color="#059669"
                            bgColor="#ecfdf5"
                            delay={0.1}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Débitos Mês"
                            value={debitosMes}
                            icon={<ArrowDownOutlined />}
                            color="#e11d48"
                            bgColor="#fff1f2"
                            delay={0.2}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Saldo Final"
                            value={saldoMes}
                            icon={<WalletOutlined />}
                            color={finalColor}
                            bgColor={finalBg}
                            delay={0.25}
                        />
                    </Col>

                    {pendenciesCount > 0 && (
                        <Col xs={24}>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <Card 
                                    size="small"
                                    style={{ 
                                        background: 'linear-gradient(90deg, #fffbeb 0%, #ffffff 100%)',
                                        border: '1px solid #fde68a',
                                        borderRadius: '12px',
                                        marginTop: 8
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Space>
                                            <ExclamationCircleOutlined style={{ color: '#d97706', fontSize: '18px' }} />
                                            <Text strong style={{ color: '#92400e', fontSize: '13px' }}>
                                                Atenção: Existem {pendenciesCount} lançamentos pendentes de meses anteriores.
                                            </Text>
                                        </Space>
                                        <Tooltip title="Estes lançamentos precisam de classificação para fechar o balancete corretamente.">
                                            <InfoCircleOutlined style={{ color: '#d97706', cursor: 'help' }} />
                                        </Tooltip>
                                    </div>
                                </Card>
                            </motion.div>
                        </Col>
                    )}
                </Row>
            </Skeleton>
        </div>
    );
}
